import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { LockKey } from "@phosphor-icons/react";

/** Full-screen blocking gate shown when an admin has flagged the account
 *  with `must_change_password`. The user cannot use the app until they set
 *  a new password. */
export default function ForcePasswordChangeGate() {
  const { user, refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Universal signal — customer users use `must_change_password`, internal
  // staff mirror the same flag (and also carry the legacy `force_password_reset`
  // for backwards compatibility).
  const mustChange = !!(user && (user.must_change_password || user.force_password_reset));
  if (!user || !mustChange) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      await refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="force-password-change-gate">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" role="dialog" aria-modal="true" aria-label="Password change required">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center"><LockKey size={20} weight="bold" /></div>
          <div>
            <div className="font-display font-bold text-[17px]">Password change required</div>
            <div className="text-[12px] text-slate-500">Your administrator requires you to set a new password before continuing.</div>
          </div>
        </div>
        <div className="p-6 space-y-3">
          <input type="password" required autoComplete="current-password" className="input w-full" placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="force-pw-current" />
          <input type="password" required autoComplete="new-password" className="input w-full" placeholder="New password (min 8 characters)" value={next} onChange={(e) => setNext(e.target.value)} data-testid="force-pw-new" />
          <input type="password" required autoComplete="new-password" className="input w-full" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="force-pw-confirm" />
          {err && <div className="text-[13px] text-red-600" data-testid="force-pw-error">{err}</div>}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          <button type="button" onClick={logout} className="text-[12px] text-slate-500 hover:underline" data-testid="force-pw-signout">Sign out instead</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="force-pw-submit">{busy ? "Saving…" : "Set new password"}</button>
        </div>
      </form>
    </div>
  );
}
